import React from 'react'
import useLimitedMode from '../../../services/subscription/useLimitedMode'
import InputWrapper from '../../Formik/InputWrapper'
import Label from '../../Formik/Label'
import Input from '../../Formik/Input'
import { Select } from '../../Select'
import { countriesOptions } from '../../../helpers/countriesOptions'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { BillingInformation } from '../../../interfaces/billingInformation'
import Button from '../../Buttons/Button'
import useUpdateBillingInformation from '../../../services/subscription/useUpdateBillingInformation'
import Spinner from '../../UI/Spinner'

interface Props {
  billingInformation: BillingInformation
}

const CompanyBillingInformationForm = ({ billingInformation }: Props) => {
  const { t } = useTranslation()
  const { isLimited } = useLimitedMode()
  const { mutate, isLoading } = useUpdateBillingInformation()
  const validationSchema = yup.object().shape({
    companyName: yup.string().required('requiredField'),
    companyEmail: yup.string().email('invalidEmail').required('requiredField'),
    companyTaxId: yup.string().required('requiredField'),
    country: yup.string().required('requiredField'),
    state: yup.string().required('requiredField'),
    city: yup.string().required('requiredField'),
    postalCode: yup.string().required('requiredField'),
    addressLine1: yup.string().required('requiredField'),
    addressLine2: yup.string().optional(),
  })

  const formik = useFormik({
    initialValues: {
      companyName: billingInformation?.company_name ?? '',
      companyEmail: billingInformation?.company_email ?? '',
      companyTaxId: billingInformation?.company_tax_id ?? '',
      country: billingInformation?.country ?? '',
      state: billingInformation?.state ?? '',
      city: billingInformation?.city ?? '',
      postalCode: billingInformation?.postal_code ?? '',
      addressLine1: billingInformation?.address_line_1 ?? '',
      addressLine2: billingInformation?.address_line_2 ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      const data = {
        company_name: values.companyName,
        company_email: values.companyEmail,
        company_tax_id: values.companyTaxId,
        country: values.country,
        state: values.state,
        city: values.city,
        postal_code: values.postalCode,
        address_line_1: values.addressLine1,
        address_line_2: values.addressLine2,
      }

      mutate({ values: data, billingInformationId: billingInformation.id })
    },
  })
  return (
    <form className='mt-8' onSubmit={formik.handleSubmit}>
      <div className='grid sm:grid-cols-2 gap-5 sm:gap-10'>
        <div className='sm:col-span-2'>
          <InputWrapper>
            <Label text={t('companyName')} />
            <Input
              id='companyName'
              as='input'
              type='text'
              hasError={
                !!(formik.touched.companyName && formik.errors.companyName)
              }
              {...formik.getFieldProps('companyName')}
            />
          </InputWrapper>
        </div>
        <InputWrapper>
          <Label text={t('companyEmail')} />
          <Input
            id='companyEmail'
            as='input'
            type='email'
            hasError={
              !!(formik.touched.companyEmail && formik.errors.companyEmail)
            }
            {...formik.getFieldProps('companyEmail')}
          />
        </InputWrapper>
        <InputWrapper>
          <Label text={t('companyTaxId')} />
          <Input
            id='companyTaxId'
            as='input'
            type='text'
            hasError={
              !!(formik.touched.companyTaxId && formik.errors.companyTaxId)
            }
            {...formik.getFieldProps('companyTaxId')}
          />
        </InputWrapper>

        <InputWrapper>
          <Label text={t('country')} />
          <Select
            isSearchable
            options={countriesOptions}
            onChange={(value: { value: string; label: string }) => {
              formik.setFieldValue('country', value.value)
            }}
            onBlur={() => {
              formik.setFieldTouched('country', true)
            }}
            value={countriesOptions.find(
              (option) => option.value === formik.values.country,
            )}
            isError={!!(formik.touched.country && formik.errors.country)}
            translateLabel={false}
          />
        </InputWrapper>
        <InputWrapper>
          <Label text={t('state')} />
          <Input
            id='state'
            as='input'
            type='text'
            hasError={!!(formik.touched.state && formik.errors.state)}
            {...formik.getFieldProps('state')}
          />
        </InputWrapper>
        <InputWrapper>
          <Label text={t('city')} />
          <Input
            id='city'
            as='input'
            type='text'
            hasError={!!(formik.touched.city && formik.errors.city)}
            {...formik.getFieldProps('city')}
          />
        </InputWrapper>
        <InputWrapper>
          <Label text={t('postalCode')} />
          <Input
            id='postalCode'
            as='input'
            type='text'
            hasError={!!(formik.touched.postalCode && formik.errors.postalCode)}
            {...formik.getFieldProps('postalCode')}
          />
        </InputWrapper>
        <div className='sm:col-span-2'>
          <InputWrapper>
            <Label text={t('address1')} />
            <Input
              id='address1'
              as='input'
              type='text'
              hasError={
                !!(formik.touched.addressLine1 && formik.errors.addressLine1)
              }
              {...formik.getFieldProps('addressLine1')}
            />
          </InputWrapper>
        </div>
        <div className='sm:col-span-2'>
          <InputWrapper>
            <Label text={t('address2')} />
            <Input
              id='address2'
              as='input'
              type='text'
              hasError={
                !!(formik.touched.addressLine2 && formik.errors.addressLine2)
              }
              {...formik.getFieldProps('addressLine2')}
            />
          </InputWrapper>
        </div>
      </div>
      <hr className='mx-auto my-5 sm:my-10 w-full border-[#E2E2E2]' />
      <Button
        type='submit'
        text={t('update')}
        disabled={isLoading || isLimited}
        title={isLimited ? t('disabledUntilActivation') : undefined}
        width='min-w-full sm:min-w-[180px] sm:ml-auto'
      >
        {isLoading && <Spinner spinner='form' color='#0C75FF' />}
      </Button>
    </form>
  )
}

export default CompanyBillingInformationForm
